import { formatStepNumber } from 'PDF/helpers/formatStepNumber';
import { ITimelineItem, StageStatusEnum, TimelineType } from 'PDF/types/timeline';

const sortTimelineItemsByDate = (items: ITimelineItem[]) =>
  items.sort((a: ITimelineItem, b: ITimelineItem) => {
    return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
  });

const quarterOfYear = (date: Date) => Math.ceil((date.getMonth() + 1) / 3);

export const getHeaderMonthDates = (items: ITimelineItem[]) => {
  const headerDates = new Set(
    items
      .flatMap((item) => [new Date(item.startDate), new Date(item.endDate)])
      .sort((a: Date, b: Date) => a.getTime() - b.getTime())
      .map((date) => `${formatStepNumber(date.getMonth() + 1)}.${date.getFullYear()}`),
  );

  return Array.from(headerDates);
};

export const getHeaderQuarterDates = (items: ITimelineItem[]) => {
  const headerDates = new Set(
    items
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .flatMap((item) => {
        const startDate = new Date(item.startDate);
        const endDate = new Date(item.endDate);

        return [
          `Q${quarterOfYear(startDate)}.${startDate.getFullYear()}`,
          `Q${quarterOfYear(endDate)}.${endDate.getFullYear()}`,
        ];
      }),
  );

  return Array.from(headerDates);
};

export const getHeaderHalfYearDates = (items: ITimelineItem[]) => {
  const headerDates = new Set(
    items
      .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
      .flatMap((item) => {
        const startDate = new Date(item.startDate);
        const endDate = new Date(item.endDate);

        return [
          `H${startDate.getMonth() + 1 > 6 ? 2 : 1}.${startDate.getFullYear()}`,
          `H${endDate.getMonth() + 1 > 6 ? 2 : 1}.${endDate.getFullYear()}`,
        ];
      }),
  );

  return Array.from(headerDates);
};

export const getTimelineGroups = (items: ITimelineItem[]) => {
  const groupedItems = Object.groupBy(items, (item) => item.status || '');

  return [
    ...sortTimelineItemsByDate(groupedItems[StageStatusEnum.DONE] ?? []),
    ...sortTimelineItemsByDate(groupedItems[StageStatusEnum.IN_PROGRESS] ?? []),
    ...sortTimelineItemsByDate(groupedItems[StageStatusEnum.TO_DO] ?? []),
    ...sortTimelineItemsByDate(groupedItems[''] ?? []),
  ];
};

export const checkMonthForDates = (startDate: Date, endDate: Date, currentDate: string) => {
  const [currentMonth, currentYear] = currentDate.split('.').map(Number);

  const firstDayOfStartMonth = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const lastDayOfEndMonth = new Date(endDate.getFullYear(), endDate.getMonth() + 1, 0);
  const firstDayOfCurrentMonth = new Date(currentYear, currentMonth - 1, 1);

  return firstDayOfCurrentMonth >= firstDayOfStartMonth && firstDayOfCurrentMonth <= lastDayOfEndMonth;
};

export const checkQuarterForDates = (startDate: Date, endDate: Date, currentQuarter: string) => {
  const [quarter, year] = currentQuarter.replace('Q', '').split('.').map(Number);

  const startCurrentQuarter = new Date(year, quarter * 3, 1);
  const startDateQuarter = quarterOfYear(startDate);
  const startFullQuarter = new Date(startDate.getFullYear(), startDateQuarter * 3, 1);
  const endDateQuarter = quarterOfYear(endDate);
  const endStartFullQuarter = new Date(endDate.getFullYear(), endDateQuarter * 3, 1);
  const endFinishFullQuarter = new Date(endStartFullQuarter.getFullYear(), endStartFullQuarter.getMonth() + 3, 0);

  return startCurrentQuarter >= startFullQuarter && startCurrentQuarter <= endFinishFullQuarter;
};

export const checkHalfYearForDates = (startDate: Date, endDate: Date, currentHalfYear: string) => {
  const [halfYear, year] = currentHalfYear.replace('H', '').split('.').map(Number);

  const startCurrentHalfYear = new Date(year, halfYear === 2 ? 6 : 0, 1);
  const startDateHalfYear = new Date(startDate.getFullYear(), startDate.getMonth() > 5 ? 6 : 0, 1);
  const endDateHalfYear = new Date(endDate.getFullYear(), endDate.getMonth() > 5 ? 6 : 0, 1);

  return startCurrentHalfYear >= startDateHalfYear && startCurrentHalfYear <= endDateHalfYear;
};

export const getHeaderDates = (type: TimelineType, items: ITimelineItem[]): string[] => {
  switch (true) {
    case type === 'halfYear':
      return getHeaderHalfYearDates(items);
    case type === 'quarter':
      return getHeaderQuarterDates(items);
    default:
      return getHeaderMonthDates(items);
  }
};

export const getDisplayConditionFunc = (
  type: TimelineType,
): ((startDate: Date, endDate: Date, currentQuarter: string) => boolean) => {
  switch (true) {
    case type === 'halfYear':
      return checkHalfYearForDates;
    case type === 'quarter':
      return checkQuarterForDates;
    default:
      return checkMonthForDates;
  }
};
